import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { OmniLogo } from "../components/OmniLogo";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePageTitle } from "../lib/usePageTitle";
import { Send, Plus, Trash2, Sparkles, Loader2, MessageSquare, User, Bot, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_MODEL = "claude-sonnet-4-5";

export default function OmniAIPage() {
  usePageTitle("OmniAI");
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [modelOpen, setModelOpen] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { navigate("/login", { state: { from: "/ai" } }); return; }
    api.get("/ai/sessions").then(({ data }) => setSessions(data));
    api.get("/ai/models").then(({ data }) => setModels(data.models || []));
    // restore last model from localStorage
    const saved = localStorage.getItem("omni:ai:model");
    if (saved) setModel(saved);
    // prefill prompt from query
    const initial = params.get("prompt");
    if (initial) setInput(initial);
  }, [user, loading, navigate, params]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);
  useEffect(() => { localStorage.setItem("omni:ai:model", model); }, [model]);

  const openSession = async (id) => {
    setCurrentId(id);
    const { data } = await api.get(`/ai/sessions/${id}`);
    setMessages(data.messages || []);
  };

  const newSession = () => { setCurrentId(null); setMessages([]); setInput(""); };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat-v2", { message: text, session_id: currentId, model });
      setCurrentId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.response, model: data.model }]);
      api.get("/ai/sessions").then(({ data: s }) => setSessions(s));
    } catch (err) {
      toast.error("L'assistant n'a pas pu répondre");
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue." }]);
    } finally { setSending(false); }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Supprimer cette conversation ?")) return;
    await api.delete(`/ai/sessions/${id}`);
    setSessions((s) => s.filter((x) => x.session_id !== id));
    if (currentId === id) newSession();
  };

  if (loading || !user || user === false) return null;

  const currentModel = models.find((m) => m.id === model) || { name: "Claude Sonnet 4.5", color: "#D97706" };

  return (
    <div className="min-h-screen flex flex-col" data-testid="ai-page">
      <Header compact />
      <div className="flex-1 flex min-h-0">
        {/* Sidebar */}
        <aside className="w-64 border-r border-[var(--omni-border)] p-3 hidden md:flex flex-col">
          <button
            onClick={newSession}
            data-testid="ai-new-session"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-sm mb-3 text-sm font-medium"
          >
            <Plus size={15} /> Nouvelle conversation
          </button>

          {/* Model selector */}
          <div className="relative mb-3" data-testid="ai-model-selector">
            <button
              onClick={() => setModelOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[var(--omni-border)] hover:bg-[var(--omni-surface)] text-sm"
              data-testid="ai-model-toggle"
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: currentModel.color }} />
                <span className="truncate">{currentModel.name}</span>
              </span>
              <ChevronDown size={13} className={`text-gray-400 transition ${modelOpen ? "rotate-180" : ""}`} />
            </button>
            {modelOpen && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-[var(--omni-border)] rounded-lg shadow-lg z-20 overflow-hidden">
                {models.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setModel(m.id); setModelOpen(false); }}
                    data-testid={`ai-model-${m.id}`}
                    className={`w-full text-left px-3 py-2 hover:bg-[var(--omni-surface)] text-sm flex items-center gap-2 ${model === m.id ? "bg-[var(--omni-surface)] font-semibold" : ""}`}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: m.color }} />
                    <div className="min-w-0">
                      <div className="truncate">{m.name}</div>
                      <div className="text-[10px] text-gray-400">{m.provider}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto space-y-0.5">
            {sessions.length === 0 && <div className="text-xs text-gray-400 px-3 py-4">Aucune conversation pour l'instant.</div>}
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => openSession(s.session_id)}
                data-testid={`ai-session-${s.session_id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-[var(--omni-surface)] ${currentId === s.session_id ? "bg-[var(--omni-surface)] font-medium" : ""}`}
              >
                <MessageSquare size={13} className="text-gray-400 flex-shrink-0" />
                <span className="flex-1 truncate">{s.title}</span>
                <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500" data-testid={`ai-delete-${s.session_id}`}>
                  <Trash2 size={12} />
                </button>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <main className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto p-6 sm:p-10 max-w-3xl w-full mx-auto">
            {messages.length === 0 && (
              <div className="text-center py-16">
                <OmniLogo brand="ai" size={88} className="mx-auto mb-4" />
                <h1 className="text-4xl font-black tracking-tight">Bonjour, je suis <span style={{ color: "#7C3AED" }}>OmniAI</span></h1>
                <p className="text-gray-500 mt-2">Posez-moi n'importe quelle question — propulsé par <span style={{ color: currentModel.color, fontWeight: 600 }}>{currentModel.name}</span>.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8 max-w-xl mx-auto text-left">
                  {[
                    "Résume-moi la théorie de la relativité",
                    "Écris-moi un haïku sur l'automne",
                    "Explique React 19 en 3 phrases",
                    "Idée de recette avec pommes de terre",
                  ].map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      data-testid={`ai-suggestion-${s.slice(0,20).replace(/\W+/g,"-")}`}
                      className="text-sm p-3 rounded-lg border border-[var(--omni-border)] hover:border-[#7C3AED] transition text-gray-700"
                    >
                      <Sparkles size={13} className="inline text-[#7C3AED] mr-1" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 mb-6 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-black text-white" : "bg-[#7C3AED] text-white"}`}>
                  {m.role === "user" ? <User size={14} /> : <Bot size={14} />}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-black text-white" : "bg-[var(--omni-surface)] text-gray-900"}`}>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
                  {m.role === "assistant" && m.model && (
                    <div className="text-[10px] text-gray-400 mt-1.5">via {m.model}</div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3 mb-6" data-testid="ai-thinking">
                <div className="w-8 h-8 rounded-full bg-[#7C3AED] text-white flex items-center justify-center flex-shrink-0">
                  <Bot size={14} />
                </div>
                <div className="bg-[var(--omni-surface)] rounded-2xl px-4 py-3 text-sm inline-flex items-center text-gray-600">
                  <Loader2 className="animate-spin mr-2" size={14} /> Réflexion…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-[var(--omni-border)] bg-white p-3 sm:p-4">
            <form onSubmit={send} className="max-w-3xl mx-auto flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Posez votre question…  (Entrée pour envoyer, Maj+Entrée pour ligne)"
                rows={1}
                className="resize-none rounded-2xl border-[var(--omni-border)] focus-visible:ring-[#7C3AED]"
                data-testid="ai-input"
              />
              <Button type="submit" disabled={sending || !input.trim()} className="rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white h-12 w-12 p-0" data-testid="ai-send-btn">
                <Send size={16} />
              </Button>
            </form>
            <p className="text-[10px] text-center text-gray-400 mt-2 max-w-3xl mx-auto">OmniAI peut faire des erreurs · Modèle : {currentModel.name}</p>
          </div>
        </main>
      </div>
    </div>
  );
}
